import { Injectable } from '@angular/core';
import { map, Observable } from 'rxjs';
import { AddressModel } from '../../../../../../common/models/address.model';
import { CarrierModel } from '../../../../../../common/models/carrier.model';
import { DATEPICKER_DISPLAY_DATE_FORMAT } from '../../../../../../common/constants/date-config';
import { getQueryStringFromObject, transformAddressDetailsIntoModel } from '../../../../../../common';
import { API_URL } from '../../../../../../services/config';
import { WrapperHttpService } from '../../../../../services/wrapper-http-service';
import { AddressDetailsModel } from '../../../../address/models/address.model';
import { CalendarBasedUliCreationFormModel, CreateAndAllocateUliRequestModel } from '../../models';
import { UliEndpoint } from '../constants/uli-endpoint';
import { ActiveCompartmentsModel, UliCreationResponse, UliFormModel, UliRule } from '../models/uli-creation.model';
import { MultiStopUliFormModel } from '../models/uli-multi-stop-creation.model';
import { format } from 'date-fns';
import { HubTrucksModel } from '../../../../administration';

@Injectable({
  providedIn: 'root',
})
export class UliCreationService {
  private readonly uliRuleStorageKey = 'uli-creation-uli-rule';
  private readonly uliFromCalendarStorageKey = 'uli-creation-uli-calendar';

  constructor(private http: WrapperHttpService) {}

  getCompartmentList(hubId: number): Observable<ActiveCompartmentsModel[]> {
    return this.http
      .withLoading()
      .get(`${API_URL}${UliEndpoint.Hubs}/${hubId}${UliEndpoint.Compartments}${UliEndpoint.Active}`);
  }

  getDeliveryAddressesList(searchStr: string, limit?: number): Observable<AddressModel[]> {
    return this.http
      .withLoading()
      .get<AddressDetailsModel[]>(
        `${API_URL}${UliEndpoint.Addresses}?` +
          getQueryStringFromObject({ filter: searchStr, limit: limit ? limit : 10 }),
      )
      .pipe(map((response: AddressDetailsModel[]) => transformAddressDetailsIntoModel(response)));
  }

  getCarriers(searchStr: string, limit?: number): Observable<CarrierModel[]> {
    return this.http
      .withLoading()
      .get(
        `${API_URL}${UliEndpoint.WebUli}${UliEndpoint.Carriers}?` +
          getQueryStringFromObject({ filter: searchStr, limit: limit ? limit : 10 }),
      );
  }

  createUli(body: UliFormModel): Observable<UliCreationResponse> {
    return this.http.withLoading().post(`${API_URL}${UliEndpoint.WebUli}`, body);
  }

  createAndAllocateUli(body: CreateAndAllocateUliRequestModel): Observable<CreateAndAllocateUliRequestModel> {
    return this.http.withLoading().post(`${API_URL}${UliEndpoint.WebUli}${UliEndpoint.CreateAndAllocate}`, body);
  }

  createMultiStopUli(body: MultiStopUliFormModel): Observable<UliCreationResponse> {
    return this.http.withLoading().post(`${API_URL}${UliEndpoint.WebUli}${UliEndpoint.MultiStop}`, body);
  }

  getHubTruckMapping(hubId: number): Observable<HubTrucksModel> {
    return this.http.withLoading().get(`${API_URL}${UliEndpoint.Hubs}/${hubId}${UliEndpoint.Trucks}`);
  }

  isLoadExisting(loadNumber: string): Observable<boolean> {
    return this.http
      .withLoading()
      .get(`${API_URL}${UliEndpoint.WebUli}${UliEndpoint.LoadExist}?` + getQueryStringFromObject({ loadNumber }));
  }

  saveUliRule(uliRule: UliRule): void {
    localStorage.setItem(this.uliRuleStorageKey, JSON.stringify(uliRule));
  }

  saveUliCalendar(uliCalendar: CalendarBasedUliCreationFormModel): void {
    localStorage.setItem(this.uliFromCalendarStorageKey, JSON.stringify(uliCalendar));
  }

  loadUliRule(): UliRule {
    const storedUliRuleData = localStorage.getItem(this.uliRuleStorageKey);

    if (storedUliRuleData) {
      return new UliRule(JSON.parse(storedUliRuleData));
    }

    return undefined;
  }

  loadUliCalendar(): CalendarBasedUliCreationFormModel {
    const storedCalendarUli = localStorage.getItem(this.uliFromCalendarStorageKey);

    if (storedCalendarUli) {
      const parsedData = JSON.parse(storedCalendarUli);

      const timeslotStart = new Date(parsedData.timeslotStart);
      const timeslotEnd = new Date(parsedData.timeslotEnd);

      format(timeslotEnd, DATEPICKER_DISPLAY_DATE_FORMAT);
      format(timeslotStart, DATEPICKER_DISPLAY_DATE_FORMAT);

      return new CalendarBasedUliCreationFormModel({
        ...parsedData,
        timeslotStart,
        timeslotEnd,
      });
    }
  }

  clearStorage(): void {
    localStorage.removeItem(this.uliRuleStorageKey);
    localStorage.removeItem(this.uliFromCalendarStorageKey);
  }
}
